import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const ToastContext = createContext(null);

function buildToast(message, type = "info", duration = 3200) {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    message,
    type,
    duration
  };
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const pushToast = useCallback((message, type = "info", duration = 3200) => {
    const toast = buildToast(message, type, duration);
    setToasts((prev) => [...prev, toast]);

    window.setTimeout(() => {
      removeToast(toast.id);
    }, duration);

    return toast.id;
  }, [removeToast]);

  const api = useMemo(() => {
    return {
      showToast: pushToast,
      success: (message, duration) => pushToast(message, "success", duration),
      error: (message, duration) => pushToast(message, "error", duration),
      info: (message, duration) => pushToast(message, "info", duration),
      warning: (message, duration) => pushToast(message, "warning", duration)
    };
  }, [pushToast]);

  return (
    <ToastContext.Provider value={api}>
      {children}

      <div className="toast-stack">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast-item toast-${toast.type}`}
          >
            <div className="toast-message">{toast.message}</div>
            <button
              className="toast-close-btn"
              onClick={() => removeToast(toast.id)}
              aria-label="Close toast"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);

  if (!ctx) {
    throw new Error("useToast must be used inside ToastProvider");
  }

  return ctx;
}